import { useLocation } from "react-router";
import { useStoreDispatch } from "../../../store/hooks";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { deviceVersionSelector } from "../../../store/filters/Nodes/selectors";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import {
  distributedNodeSelector,
  isOpenDuDrawerSelector,
  radioFirstNrCellSelector,
  radioSecondNrCellSelector,
  selectedDeviceSelector,
  selectedRnCellConfigSelector,
} from "../../../store/api/devices/selectors";
import {
  useGetDusListQuery,
  useGetNrCellListMutation,
  useSetDeviceConfigMutation,
  useSetRequestPartialRefreshMutation,
} from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { IDusNodeDataGridRow, IFullDeviceDetails, INrCell, IRadios, ISectorProvision } from "../../../store/api/deviceConfig/types";
import { setOpenDuDrawer, setRadioFirstNrCell } from "../../../store/api/devices/reducers";
import { API, PROVISION_CHECK_VAL } from "../../../globals/globals";
import { OPERATIONTYPES } from "../../../globals/constants";
import { setBasicDialogOpenFor, setOpenToast } from "../../../store/helpers/helperReducers";
import {
  prepareDistributedNodePayload,
  prepareRadioNrCellPayload,
  prepareRemoveRadioNrCellPayload,
  prepareSectorProvisionPayload,
} from "../../../components/Configuration/helpers";
import { errorMessage } from "../../../utils/helpers";
import { radioNodesColumns } from "../../../components/Shared/Table/Columns";
import { IDuCellInfo, IRadioNodeCellConfig } from "../../../store/api/devices/types";
import { IOption } from "../../../components/Shared/Filter/types";
import { IKeyValueAny } from "../../../components/Administrations/types";
import { initialDuCellInfo } from "../helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { initialState } from "../../../store/helpers/helperInitialState";

export const useDusNodesData = () => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const locationPath = location?.pathname.split("/");
  const swVersion = useSelector(deviceVersionSelector);
  const { dus = initialState.pagination } = useSelector(paginationModelSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [selectedRowData, setSelectedRowData] = useState<IDusNodeDataGridRow>();
  const [rowClicked, setRowClicked] = useState(false);

  const selectedDevice = useSelector(selectedDeviceSelector);
  const [dataForPartialRefresh, setDataForPartialRefresh] = useState<Array<IDusNodeDataGridRow>>();
  const [setPartialRefresh] = useSetRequestPartialRefreshMutation();

  const args = {
    filters: `page=${dus.page.toString()}&size=${dus.pageSize.toString()}&swVersion=${swVersion}`,
    deviceKey: locationPath[2],
  };

  const { data, isLoading } = useGetDusListQuery(args, {
    pollingInterval: API.ENDPOINTS.RADIONODES.pollingInterval || API.POLLING_INTERVAL,
  });

  useEffect(() => {
    if (!rowClicked) {
      dispatch(setRadioFirstNrCell(initialDuCellInfo));
    }
  }, [dispatch, rowClicked]);

  const partialRefresh = useCallback(async () => {
    if (!dataForPartialRefresh || dataForPartialRefresh.length === 0) {
      return;
    }

    const radioNodePath = "InternetGatewayDevice.RadioNode";
    const partialRefreshPath: string[] = dataForPartialRefresh.flatMap((data) => [`${radioNodePath}.${data.id}.MfgData.ModelNumber`]);

    if (partialRefreshPath.length > 0) {
      setPartialRefresh({ deviceKey: selectedDevice.objectKey, dataPathList: partialRefreshPath });
    }
  }, [dataForPartialRefresh, selectedDevice.objectKey, setPartialRefresh]);

  useEffect(() => {
    setTimeout(() => {
      partialRefresh();
    }, 5000);
  }, [partialRefresh]);

  return {
    data,
    isLoading,
    permissions,
    radioNodesColumns,
    rowClicked,
    setRowClicked,
    selectedRowData,
    setSelectedRowData,
    setDataForPartialRefresh,
  };
};

interface IPayload {
  [key: string]: string;
}

export const useDuNodeDrawer = (rnModelKey: string, radioUnitIndex: number) => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const locationPath = location?.pathname.split("/");
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const selectedRnCellConfigData = useSelector(selectedRnCellConfigSelector);
  const distributedUnit: IDusNodeDataGridRow = useSelector(distributedNodeSelector);
  const radioFirstNrCellDetails: INrCell = useSelector(radioFirstNrCellSelector);
  const radioSecondNrCellDetails: INrCell = useSelector(radioSecondNrCellSelector);
  const [isCreateCellRadioInfoOne, setIsCreateCellRadioInfoOne] = useState(false);
  const [isCreateCellRadioInfoTwo, setIsCreateCellRadioInfoTwo] = useState(false);
  const [selectedRadioNode, setSelectedRadioNode] = useState<IRadioNodeCellConfig>(selectedRnCellConfigData);
  const [zoneOption, setZoneOption] = useState<IOption[]>([]);
  const [zones, setZones] = useState<IOption[]>([]);
  const [selectedBand1, setSelectedBand1] = useState("");
  const [selectedBand2, setSelectedBand2] = useState("");
  const {
    phyCellIDInUse: cellPciFirst,
    referenceSignalPowerInUse: cellPowerFirst,
    txPowerPercentage: txPowerFirst,
    pbchPowerInUse: pbchPowerOne,
  } = radioFirstNrCellDetails?.nrcellConfig?.ng?.ngran?.ngranRf || {};
  const {
    phyCellIDInUse: cellPciSecond,
    referenceSignalPowerInUse: cellPowerSecond,
    txPowerPercentage: txPowerSecond,
    pbchPowerInUse: pbchPowerTwo,
  } = radioSecondNrCellDetails?.nrcellConfig?.ng?.ngran?.ngranRf || {};
  const { cellId: commonCellIdFirst } = radioFirstNrCellDetails?.nrcellConfig?.ng?.ngran?.ngranCommon || {};
  const { cellId: commonCellIdSecond } = radioSecondNrCellDetails?.nrcellConfig?.ng?.ngran?.ngranCommon || {};
  const [selectedRadio, setSelectedRadio] = useState("radio1");
  const [isAddEditRadioOne, setIsAddEditRadioOne] = useState("add");
  const [isAddEditRadioTwo, setIsAddEditRadioTwo] = useState("add");
  const [nrCellListData, setNrCellListData] = useState<string[]>([]);
  const [nrCellRowsData, setNrCellRowsData] = useState<INrCell[]>([]);
  const [radios, setRadios] = useState<IRadios[]>([]);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [errMsg2, setErrMsg2] = useState<IPayload>({});
  const [isLoadingConfigStatus, setIsLoadingConfigStatus] = useState<boolean>(false);
  const [isLoadingCellInfo, setIsLoadingCellInfo] = useState<boolean>(true);
  const [cellStatus, setCellStatus] = useState("");
  const [duBandData, setDuBandData] = useState("");
  const initialCellOne = {
    cellId: radioFirstNrCellDetails?.index || "",
    cellName: radioFirstNrCellDetails?.name || "",
    cellPci: cellPciFirst || "",
    cellDescription: radioFirstNrCellDetails?.description || "",
    txPower: txPowerFirst || "",
    commonCellId: commonCellIdFirst || "",
    power: cellPowerFirst || "",
    pbchPower: pbchPowerOne || "",
    zone: "",
  };
  const initialCellTwo = {
    cellId: radioSecondNrCellDetails?.index || "",
    cellName: radioSecondNrCellDetails?.name || "",
    cellPci: cellPciSecond || "",
    cellDescription: radioSecondNrCellDetails?.description || "",
    txPower: txPowerSecond || "",
    commonCellId: commonCellIdSecond || "",
    power: cellPowerSecond || "",
    pbchPower: pbchPowerTwo || "",
    zone: "",
  };
  const [radioNodeFormData, setRadioNodeFormData] = useState<IDusNodeDataGridRow>({
    id: 0,
    name: "",
    description: "",
    status: "",
    enable: false,
    ethernetId: "",
    ipAddress: "",
    model: "",
    nodeType: "",
    radios: [],
    radioUnit: null,
  });
  const [cellFormDataOne, setCellFormDataOne] = useState<IDuCellInfo>({
    cellId: radioFirstNrCellDetails?.index || "",
    cellName: radioFirstNrCellDetails?.name || "",
    cellPci: cellPciFirst || "",
    cellDescription: radioFirstNrCellDetails?.description || "",
    txPower: txPowerFirst || "",
    commonCellId: commonCellIdFirst || "",
    power: cellPowerFirst || "",
    pbchPower: pbchPowerOne || "",
    zone: "",
  });
  const [cellFormDataTwo, setCellFormDataTwo] = useState<IDuCellInfo>({
    cellId: radioSecondNrCellDetails?.index || "",
    cellName: radioSecondNrCellDetails?.name || "",
    cellPci: cellPciSecond || "",
    cellDescription: radioSecondNrCellDetails?.description || "",
    txPower: txPowerSecond || "",
    commonCellId: commonCellIdSecond || "",
    power: cellPowerSecond || "",
    pbchPower: pbchPowerTwo || "",
    zone: "",
  });

  const isOpenDuDrawer = useSelector(isOpenDuDrawerSelector);

  const [sectorHubs, setSectorHubs] = useState<any>([]);
  const [provisionIndex, setProvisionIndex] = useState<number>(0);
  const [errProvision, setErrProvision] = useState<IPayload>({});
  const [radioNodeList, setRadioNodeList] = useState<Array<IDusNodeDataGridRow>>([]);
  const [sectorFormData, setSectorFormData] = useState<ISectorProvision>({
    name: "",
    ethernetId: "",
    index: 1,
    radioNodeIndex: radioUnitIndex || 0,
  });

  const [getNrCellList, setNrListStatus] = useGetNrCellListMutation();
  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  const nrDefaultPageSize = {
    page: 0,
    pageSize: 100,
  };

  useEffect(() => {
    if (distributedUnit?.radioUnit && distributedUnit?.radioUnit?.radios?.length > 0) {
      let radioListArray = distributedUnit?.radioUnit?.radios?.filter((el: IRadios) => el?.band?.toLowerCase() === "nr-band-77");
      if (radioListArray?.length > 0) setRadios([...radioListArray]);
    }
  }, [distributedUnit]);

  const nrArgs = {
    filters: `page=${nrDefaultPageSize.page.toString()}&size=${nrDefaultPageSize.pageSize.toString()}`,
    deviceKey: locationPath[2],
  };

  const fetchNrCellListData = async () => {
    try {
      const result: any = await getNrCellList(nrArgs);
      if (result?.error) throw result?.error?.message;
      if (result.data?.data?.length > 0) {
        setNrCellListData(() => result?.data?.data?.map((el: INrCell) => el?.index));
        setNrCellRowsData(result?.data?.data);
        let cellObj: any = {};
        cellObj = result?.data?.data?.find((el: INrCell) => Number(el?.radioNode) === radioUnitIndex);
        if (!!cellObj?.index) {
          dispatch(setRadioFirstNrCell(cellObj));
          let resPayload = {
            cellId: cellObj?.index || "",
            cellName: cellObj?.name || "",
            cellPci: cellObj?.nrcellConfig?.ng?.ngran?.ngranRf?.phyCellIDInUse || "",
            cellDescription: cellObj?.description || "",
            power: cellObj?.nrcellConfig?.ng?.ngran?.ngranRf?.referenceSignalPowerInUse || "",
            txPower: cellObj?.nrcellConfig?.ng?.ngran?.ngranRf?.txPowerPercentage || "",
            pbchPower: cellObj?.nrcellConfig?.ng?.ngran?.ngranRf?.pbchPowerInUse || "",
            commonCellId: cellObj?.nrcellConfig?.ng?.ngran?.ngranCommon?.cellId || "",
          };
          setCellStatus(cellObj?.operState || "");
          setIsAddEditRadioOne("edit");
          setIsCreateCellRadioInfoOne(true);
          setCellFormDataOne({ ...cellFormDataOne, ...resPayload });
          setIsLoadingCellInfo(false);
        } else setIsLoadingCellInfo(false);
      } else setIsLoadingCellInfo(false);
    } catch (error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      setIsLoadingCellInfo(false);
    }
  };

  useEffect(() => {
    isOpenDuDrawer && fetchNrCellListData();
  }, [isOpenDuDrawer]);

  const handleCreateCell = (radioType: string) => {
    let res = "both";
    if (radioType === "radio1") {
      if (Array.isArray(radios) && radios?.length > 0 && radios[0].index) {
        if (radioFirstNrCellDetails?.radio !== "1") {
          res = "cell";
        }
      }
    }
    return res;
  };

  const onBlurHandler = (event: any) => {
    if (selectedRadio === "radio1") {
      const { name, value } = event?.target;
      if (name === "cellId") {
        if (nrCellListData?.includes(value)) {
          setErrMsg({ [name]: `This cellId already exist. Try another!!` });
          return;
        } else {
          setErrMsg({ [name]: `` });
          return;
        }
      }
    } else {
      const { name, value } = event?.target;
      if (name === "cellId") {
        if (nrCellListData?.includes(value)) {
          setErrMsg2({ [name]: `This cellId already exist. Try another!!` });
          return;
        } else {
          setErrMsg2({ [name]: `` });
          return;
        }
      }
    }
  };

  const handleValidateProvision = (val: number) => {
    let isValid = false;
    let radioIndexes = radioNodeList?.map((el) => el?.id);
    if (radioIndexes?.includes(val) || val >= PROVISION_CHECK_VAL) {
      isValid = false;
    } else {
      isValid = true;
    }
    return isValid;
  };

  const onBlurProvision = (event: any) => {
    const { name, value } = event?.target;
    if (name === "provisionVal") {
      const isValid = handleValidateProvision(Number(value));
      if (!isValid) {
        if (value > PROVISION_CHECK_VAL) {
          setErrProvision({ [name]: `Must be less than ${PROVISION_CHECK_VAL}` });
          return;
        } else {
          setErrProvision({ [name]: `This already exists. Try another!!` });
          return;
        }
      } else {
        setErrProvision({ [name]: `` });
        return;
      }
    }
  };

  const checkRadioValueToUpdateOnly = (formValues: any, initialValues: any) => {
    let updateOnlyRadio = false;
    for (const key in formValues) {
      if (formValues.hasOwnProperty(key) && formValues[key] !== initialValues[key]) {
        updateOnlyRadio = true;
        break;
      }
    }
    return updateOnlyRadio;
  };

  const updateCellItems = (formValues: any, initialValues: any, radioFormData: any, distributedUnit: any) => {
    const updateFields: IKeyValueAny = {};
    if (radioFormData["description"] !== distributedUnit["description"]) {
      updateFields["description"] = radioFormData["description"];
      updateFields["oldDescription"] = distributedUnit["description"];
    }
    if (radioFormData["name"] !== distributedUnit["name"]) {
      updateFields["name"] = radioFormData["name"];
      updateFields["oldName"] = distributedUnit["name"];
    }
    for (const key in formValues) {
      if (formValues.hasOwnProperty(key) && formValues[key] !== initialValues[key]) {
        updateFields[key] = formValues[key];
      }
    }
    return updateFields;
  };

  const updateProvisioning = async () => {
    setIsLoadingConfigStatus(true);
    try {
      let response: any;
      let messageStr: string = "";
      if (provisionIndex) {
        const radioNodePayload =
          !!deviceDetail?.objectKey &&
          prepareDistributedNodePayload(radioNodeFormData, deviceDetail?.objectKey, OPERATIONTYPES.CREATE, provisionIndex);
        response = await setDeviceConfig(radioNodePayload);
        messageStr = `The Provision process for RadioNode has been initiated.`;
      }

      if (response?.error) throw response.error;
      if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: messageStr,
          })
        );
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setOpenDuDrawer(false));
        setIsLoadingConfigStatus(false);
      }
    } catch (error: any) {
      setIsLoadingConfigStatus(false);
      dispatch(setBasicDialogOpenFor(undefined));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const updateSectorProvisioning = async () => {
    setIsLoadingConfigStatus(true);
    try {
      let response: any;
      let messageStr: string = "";
      if (sectorFormData?.index) {
        const radioNodePayload =
          !!deviceDetail?.objectKey &&
          prepareSectorProvisionPayload(radioNodeFormData, deviceDetail?.objectKey, OPERATIONTYPES.CREATE, sectorFormData);
        response = await setDeviceConfig(radioNodePayload);
        messageStr = `The Provision process for Sector hub has been initiated.`;
      }

      if (response?.error) throw response.error;
      if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: messageStr,
          })
        );
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setOpenDuDrawer(false));
        setIsLoadingConfigStatus(false);
      }
    } catch (error: any) {
      setIsLoadingConfigStatus(false);
      dispatch(setBasicDialogOpenFor(undefined));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleSubmitRadioNode = async (type: string) => {
    setIsLoadingConfigStatus(true);
    const operationType = type === "add" ? OPERATIONTYPES?.CREATE : OPERATIONTYPES.UPDATE;
    const formData = selectedRadio === "radio1" ? cellFormDataOne : cellFormDataTwo;
    const initialCellData = selectedRadio === "radio1" ? initialCellOne : initialCellTwo;
    const selectedBandValue = selectedRadio === "radio1" ? selectedBand1 : selectedBand2;
    const isCreateRadioCell = handleCreateCell(selectedRadio);
    const onlyRadioInfoUpt = checkRadioValueToUpdateOnly(formData, initialCellData);
    const newFormData = updateCellItems(formData, initialCellData, radioNodeFormData, distributedUnit);
    if (Object.keys(newFormData)?.length > 0) {
      const radioNodePayload = prepareRadioNrCellPayload(
        radioNodeFormData,
        formData,
        initialCellData,
        deviceDetail?.objectKey,
        operationType,
        selectedRadio,
        selectedBandValue,
        isCreateRadioCell,
        newFormData,
        onlyRadioInfoUpt,
        deviceDetail?.swVersion
      );
      try {
        let result: any;

        result = await setDeviceConfig(radioNodePayload);
        if (result?.error || configStatus?.isError) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The data has been submited`,
            })
          );
          setIsLoadingConfigStatus(false);
          dispatch(setOpenDuDrawer(false));
        }
      } catch (error: any) {
        setIsLoadingConfigStatus(false);
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  const handleDeleteRadioCell = async () => {
    setIsLoadingConfigStatus(true);
    const operationType = OPERATIONTYPES.DELETE;
    const formData = selectedRadio === "radio1" ? cellFormDataOne : cellFormDataTwo;
    const radioNodePayload = prepareRemoveRadioNrCellPayload(radioNodeFormData, formData, deviceDetail?.objectKey, operationType, selectedRadio);
    try {
      let result: any;

      result = await setDeviceConfig(radioNodePayload);
      if (result?.error || configStatus?.isError) throw result?.error;
      if (result.data?.status === 200) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The data has been submited`,
          })
        );
        setIsLoadingConfigStatus(false);
        dispatch(setOpenDuDrawer(false));
      }
    } catch (error: any) {
      setIsLoadingConfigStatus(false);
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    permissions,
    zones,
    setZones,
    zoneOption,
    setZoneOption,
    selectedRadioNode,
    setSelectedRadioNode,
    cellFormDataOne,
    setCellFormDataOne,
    cellFormDataTwo,
    setCellFormDataTwo,
    isCreateCellRadioInfoOne,
    setIsCreateCellRadioInfoOne,
    isCreateCellRadioInfoTwo,
    setIsCreateCellRadioInfoTwo,
    selectedBand1,
    setSelectedBand1,
    selectedBand2,
    setSelectedBand2,
    radioNodeFormData,
    setRadioNodeFormData,
    handleSubmitRadioNode,
    selectedRadio,
    setSelectedRadio,
    isAddEditRadioOne,
    setIsAddEditRadioOne,
    isAddEditRadioTwo,
    setIsAddEditRadioTwo,
    initialCellOne,
    initialCellTwo,
    handleDeleteRadioCell,
    onBlurHandler,
    errMsg,
    setErrMsg,
    errMsg2,
    setErrMsg2,
    isLoadingConfigStatus,
    sectorHubs,
    setSectorHubs,
    radios,
    duBandData,
    setDuBandData,
    onBlurProvision,
    updateProvisioning,
    provisionIndex,
    setProvisionIndex,
    errProvision,
    setErrProvision,
    setRadioNodeList,
    nrCellRowsData,
    sectorFormData,
    setSectorFormData,
    updateSectorProvisioning,
    isLoadingCellInfo,
    cellStatus,
  };
};
